import type { Network, TrackEdge, RoutePath } from '../data/types';

export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

/**
 * Node elevation map: smooths shared junction heights so different nominal
 * depths connect through short ramps instead of vertical steps.
 * This is a documented approximation, not a survey profile.
 */
export function buildNodeHeights(network: Network): Map<string, number> {
  const acc = new Map<string, { sum: number; n: number }>();
  for (const e of network.edges) {
    for (const node of [e.from, e.to]) {
      const a = acc.get(node) || { sum: 0, n: 0 };
      a.sum += e.height;
      a.n += 1;
      acc.set(node, a);
    }
  }
  const out = new Map<string, number>();
  for (const [node, a] of acc) out.set(node, a.sum / a.n);
  return out;
}

const RAMP_MAX = 60;

export function edgeTo3D(edge: TrackEdge, nodeHeights: Map<string, number>): Vec3[] {
  const pts = edge.coords;
  const n = pts.length;
  const yFrom = nodeHeights.get(edge.from) ?? edge.height;
  const yTo = nodeHeights.get(edge.to) ?? edge.height;
  const out: Vec3[] = [];

  // cumulative 2D distance for ramp spacing
  const cum = [0];
  for (let i = 1; i < n; i++) {
    cum.push(cum[i - 1] + Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]));
  }
  const total = cum[n - 1] || 1;
  const ramp = Math.min(RAMP_MAX, total * 0.45);

  for (let i = 0; i < n; i++) {
    const d = cum[i];
    let y = edge.height;
    const wStart = ramp > 0 ? Math.max(0, 1 - d / ramp) : 0;
    const wEnd = ramp > 0 ? Math.max(0, 1 - (total - d) / ramp) : 0;
    const w = Math.max(wStart, wEnd);
    if (w > 0) {
      const anchor = wStart >= wEnd ? yFrom : yTo;
      y = edge.height + (anchor - edge.height) * w;
    }
    out.push({ x: pts[i][0], y, z: pts[i][1] });
  }
  return out;
}

export function pathTo3D(path: RoutePath, nodeHeights: Map<string, number>): Vec3[] {
  const pts = path.coords;
  const out: Vec3[] = [];
  for (let i = 0; i < pts.length; i++) {
    let y = 0;
    // sample elevation by nearest edge is expensive; use average surface guess
    // Rendered line will be re-lifted by the renderer using per-edge heights.
    out.push({ x: pts[i][0], y, z: pts[i][1] });
  }
  return out;
}

/** cumulative arc length over a 2D polyline */
export function cumulative2D(pts: [number, number][]): number[] {
  const cum = [0];
  for (let i = 1; i < pts.length; i++) {
    cum.push(cum[i - 1] + Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]));
  }
  return cum;
}

export function sampleAt(pts: { x: number; y: number; z: number }[], cum: number[], dist: number) {
  if (pts.length === 0) return null;
  let i = 0;
  const total = cum[cum.length - 1] || 0;
  const d = Math.max(0, Math.min(total, dist));
  while (i < cum.length - 1 && cum[i + 1] < d) i++;
  const segLen = cum[i + 1] - cum[i] || 1;
  const t = (d - cum[i]) / segLen;
  const a = pts[i];
  const b = pts[i + 1] ?? a;
  return {
    x: a.x + (b.x - a.x) * t,
    y: a.y + (b.y - a.y) * t,
    z: a.z + (b.z - a.z) * t,
    heading: Math.atan2(b.x - a.x, b.z - a.z),
  };
}
